// The public Tools page (cellcog.ai/tools): ONE content file for its copy (Nitish + Maeve, Nov 3 2026; mock of record
// mocks/tools_page_v3, copy ruled verbatim; spec agent_docs/tools_page/REQUIREMENTS.md). The page, its FAQPage JSON-LD and its .md alternate (generated from the
// prerendered HTML, scripts/marketingHtmlTwins.js) all read this file, so a copy change needs no backend.
// The app count is TOOL_COUNT (content/toolCount.json, canon tool-count): never type it here.
// App names are the catalog's names (Toolkit.name, Sep 14 2026 snapshot) so the prerendered sections match the live list.
// Every featured one-liner and job step is a claim about a connector's real actions, checked against the live catalog
// (agent_docs/tools_page/CLAIMS_VERDICT.md, Maeve Nov 3 2026). Change one = re-check it there.
// Salesforce is never hand-picked here (its logo needs a licence; Nitish Nov 3 2026, the ConnectorStrip.js note): it stays
// in the full list only.
import { TOOL_COUNT } from './toolCount';

// "1,500+" -> "1,500", for the one sentence that reads "More than 1,500".
const COUNT_NUMBER = TOOL_COUNT.replace(/\+$/, '');

export const TOOLS_PAGE_PATH = '/tools';

export const TOOLS_HERO = {
  eyebrowCount: TOOL_COUNT,
  eyebrowRest: 'apps for your chats and AI employees',
  title: 'CellCog works in the apps you already use',
  sub: 'Connect an app once, then use it in any chat or hand it to an AI employee. Anything risky waits for your yes.',
  searchPlaceholder: `Search ${TOOL_COUNT} apps`,
  strip: ['gmail', 'slack', 'notion', 'hubspot', 'github', 'stripe', 'googlesheets', 'canva', 'linear', 'google_analytics', 'pipedrive', 'figma'],
};

// The nine catalog categories, in the page's order. Ids = the bucket ids of py/cellcog/services/toolkit_buckets.py (the
// /public/tools/catalog payload); labels are this page's own (mock v3).
export const TOOLS_CATEGORIES = [
  { id: 'communication', label: 'Communication' },
  { id: 'social', label: 'Marketing & social' },
  { id: 'sales', label: 'Sales & support' },
  { id: 'docs', label: 'Docs & files' },
  { id: 'productivity', label: 'Productivity' },
  { id: 'analytics', label: 'Analytics & SEO' },
  { id: 'finance', label: 'Finance & commerce' },
  { id: 'design', label: 'Design & content' },
  { id: 'code', label: 'Code & data' },
];

// Display names for every app the page shows before the catalog loads (hero strip, jobs, featured, ways).
export const APP_NAMES = {
  gmail: 'Gmail', googlecalendar: 'Google Calendar', slack: 'Slack', notion: 'Notion', googledrive: 'Google Drive',
  googlesheets: 'Google Sheets', hubspot: 'HubSpot', attio: 'Attio', apollo: 'Apollo', stripe: 'Stripe',
  quickbooks: 'QuickBooks', xero: 'Xero', github: 'GitHub', linear: 'Linear', jira: 'Jira', figma: 'Figma', canva: 'Canva',
  airtable: 'Airtable', mailchimp: 'Mailchimp', google_analytics: 'Google Analytics',
  google_search_console: 'Google Search Console', ahrefs: 'Ahrefs', linkedin: 'LinkedIn', youtube: 'YouTube',
  outlook: 'Outlook', microsoft_teams: 'Microsoft Teams', zoom: 'Zoom', calendly: 'Calendly', intercom: 'Intercom',
  zendesk: 'Zendesk', asana: 'Asana', clickup: 'ClickUp', trello: 'Trello', sentry: 'Sentry', webflow: 'Webflow',
  klaviyo: 'Klaviyo', instagram: 'Instagram', facebook: 'Facebook', pipedrive: 'Pipedrive', googledocs: 'Google Docs',
  dropbox: 'Dropbox', monday: 'Monday', todoist: 'Todoist', typeform: 'Typeform', posthog: 'PostHog', semrush: 'Semrush',
  mixpanel: 'Mixpanel', paypal: 'Paypal', brex: 'Brex', square: 'Square', pexels: 'Pexels', vimeo: 'Vimeo',
  abyssale: 'Abyssale', vercel: 'Vercel', supabase: 'Supabase',
};

export const TOOLS_JOBS_HEAD = {
  kicker: 'What it does with them',
  title: 'One request. Several apps. Finished work.',
  sub: 'CellCog moves between your tools the way you would, and brings back the result.',
  askLabel: 'Example request',
};

// Step levels follow the real threat rule: s = safe (reads everyday data or works in the chat), m = moderate (reads private
// data like email or payments), d = dangerous (any write or send).
export const TOOLS_LEVELS = {
  s: { word: 'Safe', meaning: 'reads or works in the chat' },
  m: { word: 'Moderate', meaning: 'reads private data' },
  d: { word: 'Dangerous', meaning: 'changes something in the app' },
};
export const TOOLS_LEGEND_NOTE = 'You choose what runs on its own.';

// One job per category: 6 apps, one example request, exactly 4 steps.
export const TOOLS_JOBS = {
  communication: {
    apps: ['gmail', 'outlook', 'googlecalendar', 'slack', 'microsoft_teams', 'zoom'],
    ask: "Reply to today's urgent email and book the three meetings people asked for.",
    steps: [
      ['m', "Reads today's unread mail in Gmail"],
      ['s', 'Checks Google Calendar for free times'],
      ['s', 'Drafts a reply to each urgent email in the chat'],
      ['d', 'Sends the replies and the invites'],
    ],
  },
  social: {
    apps: ['linkedin', 'instagram', 'youtube', 'facebook', 'mailchimp', 'canva'],
    ask: "Turn this week's blog post into a LinkedIn post with an image, and post it.",
    steps: [
      ['s', "Reads this week's blog post"],
      ['s', 'Writes the LinkedIn post in the chat'],
      ['d', 'Makes the image in Canva'],
      ['d', 'Posts it to LinkedIn'],
    ],
  },
  sales: {
    apps: ['apollo', 'hubspot', 'pipedrive', 'attio', 'intercom', 'zendesk'],
    ask: 'Find 20 ops leads at Texas logistics firms, add them to HubSpot, and draft an intro to each.',
    steps: [
      ['s', 'Searches Apollo for the right titles'],
      ['d', 'Adds each person to HubSpot with notes'],
      ['s', 'Writes an intro for each in the chat'],
      ['d', 'Creates a follow-up task for each in HubSpot'],
    ],
  },
  docs: {
    apps: ['googledrive', 'googledocs', 'googlesheets', 'notion', 'dropbox', 'airtable'],
    ask: 'Sum up the client feedback in our Drive folder in one doc and a tracker sheet.',
    steps: [
      ['m', 'Reads the files in your Google Drive folder'],
      ['s', 'Pulls out the common feedback'],
      ['d', 'Writes the summary in Google Docs'],
      ['d', 'Builds the tracker in Google Sheets'],
    ],
  },
  productivity: {
    apps: ['asana', 'clickup', 'trello', 'monday', 'todoist', 'typeform'],
    ask: 'Turn these meeting notes into Asana tasks with owners and due dates.',
    steps: [
      ['s', 'Reads the notes you share'],
      ['s', 'Checks Asana for the right project and people'],
      ['d', 'Creates each task with an owner and a date'],
      ['s', 'Shows you the list in the chat'],
    ],
  },
  analytics: {
    apps: ['google_analytics', 'google_search_console', 'ahrefs', 'posthog', 'semrush', 'mixpanel'],
    ask: 'Find the three pages losing the most search clicks and draft fixes for them.',
    steps: [
      ['s', 'Reads Google Analytics and Search Console'],
      ['s', 'Finds the three pages losing clicks'],
      ['s', 'Checks those pages in Ahrefs against competitors'],
      ['s', 'Drafts new titles and copy in the chat'],
    ],
  },
  finance: {
    apps: ['stripe', 'quickbooks', 'xero', 'paypal', 'brex', 'square'], // brex, not shopify: Shopify is Coming Soon, off the public list (Nov 3 2026)
    ask: "Match October's Stripe payments to our invoices and flag what doesn't line up.",
    steps: [
      ['m', "Pulls October's payments from Stripe"],
      ['m', 'Pulls the invoices from QuickBooks'],
      ['s', 'Matches them line by line'],
      ['s', 'Lists the gaps for you in the chat'],
    ],
  },
  design: {
    apps: ['canva', 'figma', 'webflow', 'pexels', 'vimeo', 'abyssale'],
    ask: 'Add our new plans to the Webflow CMS with a fresh hero image, and publish.',
    steps: [
      ['s', 'Reads the plans collection in Webflow'],
      ['s', 'Writes the new entries in the chat'],
      ['d', 'Makes the hero image in Canva'],
      ['d', 'Updates the CMS items and publishes the site'],
    ],
  },
  code: {
    apps: ['github', 'sentry', 'linear', 'vercel', 'supabase', 'jira'],
    ask: "Find the change behind today's worst error and open an issue with the details.",
    steps: [
      ['s', "Reads today's new errors in Sentry"],
      ['m', 'Finds the related change in GitHub'],
      ['s', 'Writes up the cause in the chat'],
      ['d', 'Opens an issue in Linear with the evidence'],
    ],
  },
};

export const TOOLS_FEATURED_HEAD = {
  kicker: 'Featured apps',
  title: 'The apps people connect most',
  sub: 'What CellCog can do in each one.',
};

// 36 apps, our own one-liner each. Not links (no per-app pages yet). The full list's category previews skip these.
export const TOOLS_FEATURED = [
  ['gmail', 'Read and sort mail, and draft replies for you to send.'],
  ['googlecalendar', 'Find free time and set up meetings.'],
  ['slack', 'Read channels and post updates where you choose.'],
  ['notion', 'Read and update pages and databases.'],
  ['googledrive', 'Find, read and organize your files.'],
  ['googlesheets', 'Read, update and build spreadsheets.'],
  ['hubspot', 'Look up contacts and keep deals up to date.'],
  ['pipedrive', 'Find people and keep deals up to date.'],
  ['apollo', 'Find prospects and the companies they work at.'],
  ['stripe', 'Report on revenue, payments and subscriptions.'],
  ['quickbooks', 'Pull reports and prepare invoices.'],
  ['xero', 'Prepare invoices and pull financial reports.'],
  ['github', 'Read code and open issues and pull requests.'],
  ['linear', 'Triage issues and write project updates.'],
  ['jira', 'Triage tickets and keep their status current.'],
  ['figma', 'Read your files and frames.'],
  ['canva', 'Create designs, fill your brand templates and export them.'],
  ['airtable', 'Update records and build reports from your bases.'],
  ['mailchimp', 'Build campaigns and read their results.'],
  ['google_analytics', 'Read traffic, sources and conversions.'],
  ['google_search_console', 'Read search queries, clicks and rankings.'],
  ['ahrefs', 'Research keywords, backlinks and competitors.'],
  ['linkedin', 'Publish the posts you approve.'],
  ['youtube', 'Upload videos and read channel stats.'],
  ['outlook', 'Read mail and calendar, and draft replies.'],
  ['microsoft_teams', 'Read and post in team channels.'],
  ['zoom', 'Schedule meetings.'],
  ['calendly', 'See your scheduled bookings.'],
  ['intercom', 'Read conversations and draft answers in the chat.'],
  ['zendesk', 'Triage tickets and draft replies in the chat.'],
  ['asana', 'Turn meeting notes into tasks.'],
  ['clickup', 'Create tasks and write status updates.'],
  ['trello', 'Create and move cards across boards.'],
  ['sentry', 'Triage errors and find what caused them.'],
  ['webflow', 'Update CMS items and page SEO, then publish.'],
  ['klaviyo', 'Build email flows and read results.'],
].map(([slug, line]) => ({ slug, name: APP_NAMES[slug], line }));

export const TOOLS_FEATURED_SLUGS = new Set(TOOLS_FEATURED.map((f) => f.slug));

export const TOOLS_DIRECTORY_HEAD = {
  kicker: 'The full list',
  title: `All ${TOOL_COUNT} apps`,
  sub: "Search, or browse by category. Featured apps aren't repeated here until you open a category.",
  searchPlaceholder: 'Search apps',
  allLabel: 'All',
  resultsLabel: 'Results',
  noResult: (q) => `No app called "${q}" yet. If it offers an MCP server or an API key, you can still add it.`,
  noResultLink: 'See below.',
  unavailable: "The full list didn't load. Refresh the page to try again.",
};

export const TOOLS_WAYS = {
  kicker: "Don't see your tool?",
  title: 'Three ways to connect',
  sub: 'Most apps are one click from the list. For anything else, it depends on the app: if it offers an MCP server or gives you an API key, CellCog can use it the same way. Not every app offers either.',
  cards: [
    {
      id: 'app',
      title: 'Connect an app',
      body: `Pick it from ${TOOL_COUNT} apps and sign in. You choose which chats and AI employees may use it.`,
    },
    {
      id: 'mcp',
      title: 'Add your own MCP server',
      body: 'If your own system or a vendor offers an MCP server, add its URL. Its tools show up like any other app.',
    },
    {
      id: 'key',
      title: 'Add an API key',
      body: 'If a service gives you an API key, save it here. The key stays in your account and is never shown in chat.',
    },
  ],
};

export const TOOLS_FAQ_HEAD = { eyebrow: 'FAQ', title: 'Frequently asked questions' };

// FAQPage JSON-LD is built from this exact list (the AIE_FAQ_ITEMS pattern).
export const TOOLS_FAQ_ITEMS = [
  {
    question: 'Which apps does CellCog work with?',
    answer: `More than ${COUNT_NUMBER}, from Gmail, Slack and Notion to HubSpot, Stripe and GitHub. Search the list above to check yours.`,
  },
  {
    question: 'Can I use these apps in a regular chat, or only with AI employees?',
    answer: 'Both. Connect an app once, then choose which chats and AI employees may use it.',
  },
  {
    question: 'Do I need to connect anything to start?',
    answer: 'No. CellCog can research, write and build without any apps. Connect one when you want it to work there.',
  },
  {
    question: "What if my tool isn't listed?",
    answer: 'It depends on the app. If it offers an MCP server, add it by URL; if it gives you an API key, save the key. CellCog then uses it like any other app. Some apps offer neither.',
  },
  {
    question: 'Can CellCog act in my apps without asking?',
    answer: 'You choose. Anything risky, like sending, posting or changing data, can wait for your yes.',
  },
  {
    question: 'Can I disconnect an app later?',
    answer: 'Yes, any time, from Connectors in the app.',
  },
];

export const TOOLS_CLOSE = {
  title: 'Put your apps to work',
  sub: 'Start a chat or hire an AI employee, and connect the tools you already use.',
  cta: 'Get started',
};

export const TOOLS_SEO = {
  title: `Tools: ${TOOL_COUNT} apps for your chats and AI employees | CellCog`,
  description: `Connect Gmail, Slack, HubSpot, Stripe, GitHub or any of ${TOOL_COUNT} apps once, then use them in any CellCog chat or hand them to an AI employee.`,
};

// Logos: the marks bundled with the site (public/assets/icons/connectors, the ConnectorStrip set, fetched from the same source
// as the catalog) wherever one exists, so the prerendered HTML carries real logos with no third-party request; else the
// composio logo service the in-app Connectors page also uses. Every logo sits on a LIGHT plate on both faces, so GitHub and
// Intercom take their `-light` twins (their base files are white artwork for dark surfaces).
const LOCAL_LOGOS = new Set([
  'airtable', 'asana', 'calendly', 'canva', 'clickup', 'dropbox', 'figma', 'github', 'gmail', 'googlecalendar', 'googledrive',
  'googlesheets', 'hubspot', 'intercom', 'jira', 'linkedin', 'mailchimp', 'monday', 'notion', 'quickbooks', 'shopify', 'slack',
  'stripe', 'trello', 'typeform', 'webflow', 'youtube', 'zendesk', 'zoom',
]);
const LIGHT_PLATE_TWINS = new Set(['github', 'intercom']);
export const logoFor = (slug) => {
  const s = String(slug).toLowerCase();
  if (LOCAL_LOGOS.has(s)) return `/assets/icons/connectors/${s}${LIGHT_PLATE_TWINS.has(s) ? '-light' : ''}.svg`;
  return `https://logos.composio.dev/api/${s}`;
};
